<template>
  <div class="container-nzidoo">
    <page-title title="FAQ" />

    <!-- Toolbar -->
    <div class="admin-toolbar">
      <div class="admin-search-box">
        <i class="mdi mdi-magnify admin-search-icon"></i>

        <input type="text" v-model="searchTerm" :placeholder="$t('sentence.searchByName')" class="admin-search-input">

        <!-- Clear icon -->
        <i v-if="searchTerm" class="mdi mdi-close-circle admin-clear-icon" @click="clearSearch"
          :title="$t('sentence.clear')"></i>
      </div>
      <button class="btn btn-primary" @click="openCreateModal">
        <i class="mdi mdi-plus"></i>
                <div class="d-none d-md-block">
          {{ $t("action.add") }}
        </div>
      </button>
    </div>

    <LoadingSpinner v-if="loader" />
    <!-- FAQ Grid -->
    <div class="admin-grid">
      <div v-for="faq in filteredFaqs" :key="faq.id" class="admin-card">
        <div class="admin-card-header">
          <span class="faq-card-id">{{ $t('main.category') }}: {{ faq.category || 'N/A' }}</span>
          <div class="admin-card-actions">
            <button class="btn btn-icon-action btn-warning" @click="edit(faq.id)" :title="$t('action.update')">
              <i class="mdi mdi-pencil"></i>
            </button>
            <button class="btn btn-icon-action btn-danger" @click="remove(faq.id)" :title="$t('action.delete')">
              <i class="mdi mdi-delete"></i>
            </button>
          </div>
        </div>
        <div class="admin-card-body">
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('main.question') }}</label>
            <div class="card-name">{{ faq.question }}</div>
          </div>
          <div class="admin-card-section">
            <label class="admin-card-label">{{ $t('main.answer') }}</label>
            <div class="card-light">{{ faq.answer }}</div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredFaqs.length === 0" class="admin-empty-state">
        <svg class="admin-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
        <p class="admin-empty-text">{{ $t('sentence.noResults') || 'Aucun résultat trouvé' }}</p>
      </div>
    </div>

    <!-- Drawer Backdrop -->
    <transition name="drawer-backdrop">
      <div v-if="showModal" class="admin-drawer-backdrop" @click="closeModal"></div>
    </transition>

    <!-- Drawer -->
    <transition name="drawer">
      <div v-if="showModal" class="admin-drawer-container">
        <div class="admin-drawer-header">
          <h3 class="admin-drawer-title">
            {{ isEditing ? $t('action.update') : $t('action.addFaq') }}
          </h3>
          <button class="btn btn-close" @click="closeModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <div class="admin-drawer-body">
          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('main.category') }}</label>
            <input type="text" v-model="form.category" class="admin-form-input" :placeholder="$t('main.category')">
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('main.question') }}</label>
            <input type="text" v-model="form.question" class="admin-form-input" :placeholder="$t('main.question')">
          </div>

          <div class="admin-form-group">
            <label class="admin-form-label">{{ $t('main.answer') }}</label>
            <textarea v-model="form.answer" class="admin-form-textarea" rows="6"
              :placeholder="$t('main.answer')"></textarea>
          </div>
        </div>

        <div class="admin-drawer-footer">
          <button class="btn btn-secondary" @click="closeModal">
            {{ $t('action.cancel') }}          </button>
          <button class="btn btn-primary" @click="save">
            {{ isEditing ? $t('action.update') : $t('action.create') }}
          </button>
        </div>
      </div>
    </transition>

    <notifications style="top:5%;" width="35%" group="error" position="top center" />
    <notifications style="top:5%;" width="35%" group="success" position="top center" />
  </div>
</template>

<script>
import PageTitle from '@/components/templates/PageTitle.vue';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";
import AlertService from '@/services/alertService';

export default {
  components: { PageTitle,LoadingSpinner },
  name: "FaqAdmin",

  data() {
    return {
      faqs: [],
      showModal: false,
      isEditing: false,
      editId: null,
      searchTerm: "",
      loader: false,
      form: {
        category: "",
        question: "",
        answer: ""
      }
    };
  },

  computed: {
    filteredFaqs() {
      if (!this.searchTerm) return this.faqs;

      const term=this.searchTerm.toLowerCase();
      return this.faqs.filter(faq =>
        (faq.category&&faq.category.toLowerCase().includes(term))||
        faq.question.toLowerCase().includes(term)||
        faq.answer.toLowerCase().includes(term)
      );
    }
  },

  mounted() {
    this.loadFaqs();
  },

  methods: {
    clearSearch() {
      this.searchTerm="";
    },
    async loadFaqs() {
      try {
        this.loader=true;
        const res=await this.$axios.get("/faqs");
        this.faqs=res.data;
        this.loader=false;
      } catch (err) {
        console.error("Error loading FAQs:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorLoading')||"Erreur lors du chargement",
          type: "error",
        });
      }
    },

    openCreateModal() {
      this.isEditing=false;
      this.showModal=true;
      this.form={ category: "", question: "", answer: "" };
    },

    closeModal() {
      this.showModal=false;
    },

    async save() {
      try {
        const formData=new FormData();
        formData.append("category", this.form.category||"");
        formData.append("question", this.form.question);
        formData.append("answer", this.form.answer);

        if (this.isEditing) {
          await this.$axios.post(`/admin/faq/update/${this.editId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          this.$notify({
            group: "success",
            title: this.$t('sentence.updateSuccess')||"FAQ mis à jour avec succès",
            type: "success",
          });
        } else {
          await this.$axios.post("/admin/faq/create", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          this.$notify({
            group: "success",
            title: this.$t('sentence.createSuccess')||"FAQ créé avec succès",
            type: "success",
          });
        }
        this.closeModal();
        this.loadFaqs();
      } catch (err) {
        console.error("Error saving FAQ:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorSaving')||"Erreur lors de l'enregistrement",
          type: "error",
        });
      }
    },

    async edit(id) {
      this.isEditing=true;
      this.editId=id;

      try {
        const res=await this.$axios.get(`/faqs/${id}`);
        this.form={
          category: res.data.category||"",
          question: res.data.question,
          answer: res.data.answer
        };
        this.showModal=true;
      } catch (err) {
        console.error("Error loading FAQ:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorLoading')||"Erreur lors du chargement",
          type: "error",
        });
      }
    },

    async remove(id) {
      const confirmed=await AlertService.confirm({
        title: this.$t('sentence.confirmDelete')||"Êtes-vous sûr ?",
        confirmButtonText: this.$t('action.delete')||"Supprimer",
        cancelButtonText: this.$t('action.cancel')||"Annuler",
        icon: "warning"
      });

      if (!confirmed) return;

      try {
        await this.$axios.post(`/admin/faq/delete/${id}`);
        this.$notify({
          group: "success",
          title: this.$t('sentence.deleteSuccess')||"FAQ supprimé avec succès",
          type: "success",
        });
        this.loadFaqs();
      } catch (err) {
        console.error("Error deleting FAQ:", err);
        this.$notify({
          group: "error",
          title: this.$t('sentence.errorDeleting')||"Erreur lors de la suppression",
          type: "error",
        });
      }
    }
  }
};
</script>

<style scoped src="@/assets/css/shared-admin-styles.css"></style>
<style scoped>

/* Component-specific styles */
.faq-card-id {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .admin-drawer-container {
    max-width: 100%;
  }
}
</style>